'use client'

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { Box, Button, Stack, Typography } from '@mui/material'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useState } from 'react'

interface IFilterOption {
  label: string
  value: string
  count?: number
  disabled?: boolean
}

interface SizeFilterProps {
  attributeCode: string
  label: string
  icon?: string
  defaultOpen?: boolean
  options: IFilterOption[]
  selectedValues: string[]
  onChange: (attributeCode: string, value: string) => void
}

/**
 * Component filter dạng button cho size
 */
export const SizeFilter: React.FC<SizeFilterProps> = ({
  attributeCode,
  label,
  icon,
  defaultOpen = false,
  options,
  selectedValues,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <Stack sx={{ mb: 1 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        onClick={() => setIsOpen(!isOpen)}
        sx={{ cursor: 'pointer', userSelect: 'none', py: 1 }}
      >
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'text.primary',
          }}
        >
          {icon && <Box component="span">{icon}</Box>}
          {label}
        </Typography>
        <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.3 }}>
          <KeyboardArrowDownIcon sx={{ color: 'text.secondary' }} />
        </motion.div>
      </Stack>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, py: 1 }}>
              {options.map((option) => {
                const isSelected = selectedValues.includes(option.label)
                // Disable nếu count = 0 (hết hàng)
                const isDisabled = option.disabled || option.count === 0

                return (
                  <Button
                    key={option.label}
                    disabled={isDisabled}
                    onClick={() => onChange(attributeCode, option.label)}
                    sx={{
                      minWidth: 48,
                      height: 36,
                      px: 2,
                      py: 1,
                      fontSize: '14px',
                      fontWeight: 600,
                      borderRadius: '8px',
                      border: (theme) => (isSelected ? 'none' : `1px solid ${theme.palette.grey[500]}`),
                      bgcolor: (theme) =>
                        isDisabled
                          ? theme.palette.grey[100]
                          : isSelected
                            ? theme.palette.primary.main
                            : 'transparent',
                      color: (theme) =>
                        isDisabled
                          ? theme.palette.grey[500]
                          : isSelected
                            ? theme.palette.primary.contrastText
                            : theme.palette.primary.main,
                      '&:hover': {
                        bgcolor: (theme) =>
                          isDisabled
                            ? theme.palette.grey[100]
                            : isSelected
                              ? theme.palette.primary.main
                              : theme.palette.grey[50],
                      },
                      '&.Mui-disabled': {
                        bgcolor: (theme) => theme.palette.grey[100],
                        color: (theme) => theme.palette.grey[500],
                      },
                    }}
                  >
                    {option.label}
                  </Button>
                )
              })}
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Stack>
  )
}
